import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  ArrowLeft, Star, CheckCircle, MapPin, Shield, Info,
  ExternalLink, Map, FileText, Calendar,
} from "lucide-react";

const DESIGNATION_LABEL = {
  FOUNDING_HOME_CLUB: "Founding Home Club",
  LV_PARTNER: "LV Partner",
  LV_GLOBAL_PARTNER: "LV Global Partner",
};

function HeroSection({ partner }) {
  const hasCover = !!partner.cover_image_url;
  const hasLogo = !!partner.logo_url;
  const isFounding = partner.designation === "FOUNDING_HOME_CLUB";

  if (hasCover) {
    return (
      <div className="relative -mx-4 sm:-mx-6 overflow-hidden" data-testid="partner-hero">
        <div className="h-56 sm:h-72 lg:h-80 overflow-hidden">
          <img
            src={partner.cover_image_url}
            alt={partner.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-lv-bg via-lv-bg/50 to-transparent" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 pb-6 sm:pb-8">
          <div className="flex items-end gap-4">
            {hasLogo && (
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-cream/95 rounded-full flex items-center justify-center p-2.5 shadow-xl ring-1 ring-bronze/20 shrink-0">
                <img src={partner.logo_url} alt={partner.name} className="w-full h-full object-contain" />
              </div>
            )}
            <div className="min-w-0">
              <DesignationBadge designation={partner.designation} isFounding={isFounding} />
              <h1
                className="font-serif text-2xl sm:text-3xl lg:text-4xl text-cream tracking-wide mt-1"
                data-testid="partner-name"
              >
                {partner.name}
              </h1>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative -mx-4 sm:-mx-6 overflow-hidden" data-testid="partner-hero">
      <div className="h-64 sm:h-80 lg:h-96 bg-[#0a0806] flex items-center justify-center relative">
        {hasLogo ? (
          <img
            src={partner.logo_url}
            alt={partner.name}
            className="relative h-36 sm:h-44 lg:h-52 w-auto object-contain"
          />
        ) : (
          <span className="font-serif text-6xl text-bronze/15">{partner.name?.charAt(0)}</span>
        )}
      </div>
      <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 pb-6 sm:pb-8 bg-gradient-to-t from-lv-bg via-lv-bg/80 to-transparent pt-16">
        <DesignationBadge designation={partner.designation} isFounding={isFounding} />
        <h1
          className="font-serif text-2xl sm:text-3xl lg:text-4xl text-cream tracking-wide mt-1"
          data-testid="partner-name"
        >
          {partner.name}
        </h1>
      </div>
    </div>
  );
}

function DesignationBadge({ designation, isFounding }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] tracking-[0.2em] uppercase font-medium ${
        isFounding ? "text-bronze" : "text-lv-muted"
      }`}
      data-testid="partner-designation"
    >
      {isFounding && <Star size={11} className="fill-bronze/30" />}
      {DESIGNATION_LABEL[designation] || designation}
    </span>
  );
}

function PrivilegeCard({ privilege }) {
  return (
    <div
      className="bg-walnut border border-lv-border p-6 sm:p-8"
      data-testid={`privilege-${privilege.id}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <CheckCircle size={18} className="text-lv-active shrink-0 mt-0.5" />
        <h3 className="font-serif text-lg sm:text-xl text-cream tracking-wide leading-snug">
          {privilege.title}
        </h3>
      </div>

      {privilege.description && (
        <p className="text-sm text-sand/80 leading-relaxed ml-[30px] mb-5">
          {privilege.description}
        </p>
      )}

      {privilege.how_to_use && (
        <div className="ml-[30px] bg-walnut-deep border border-lv-border/40 p-5">
          <p className="text-[10px] sm:text-[11px] tracking-[0.25em] uppercase text-bronze font-medium mb-2.5">
            How to Enjoy
          </p>
          <p className="text-sm text-sand/70 leading-relaxed">{privilege.how_to_use}</p>
        </div>
      )}

      {privilege.restrictions && (
        <p className="text-xs text-lv-muted/70 mt-4 ml-[30px] leading-relaxed">
          {privilege.restrictions}
        </p>
      )}
    </div>
  );
}

export default function PartnerDetail() {
  const { partnerId } = useParams();
  const { member } = useAuth();

  const { data: partner, isLoading, error } = useQuery({
    queryKey: ["partner", partnerId],
    queryFn: () => api.get(`/privileges/${partnerId}`),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-12 h-0.5 bg-bronze mx-auto animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-32 px-6">
        <p className="text-cream text-sm">{error.message}</p>
      </div>
    );
  }

  if (!partner) return null;

  const isActive = member?.status === "ACTIVE";
  const hasLinks = partner.google_maps_url || partner.website_url;

  return (
    <div data-testid="partner-detail-page" className="max-w-3xl mx-auto">
      {/* Back navigation */}
      <Link
        to="/privileges"
        className="inline-flex items-center gap-2 text-sm text-lv-muted hover:text-cream transition-colors mb-6 min-h-[44px]"
        data-testid="partner-back-link"
      >
        <ArrowLeft size={16} /> My Privileges
      </Link>

      {/* Hero */}
      <HeroSection partner={partner} />

      {/* Meta row: location + categories */}
      <div className="mt-6 sm:mt-8 flex flex-col gap-3">
        {partner.location && (
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-lv-muted/60 shrink-0" />
            <span className="text-sm text-lv-muted tracking-wide">{partner.location}</span>
          </div>
        )}
        {partner.categories?.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {partner.categories.map((c) => (
              <span
                key={c}
                className="px-3 py-1 text-[10px] tracking-[0.2em] uppercase text-lv-muted border border-lv-border/60 rounded-full"
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Partner links (Google Maps, Website) */}
      {hasLinks && (
        <div className="flex flex-wrap gap-3 mt-5" data-testid="partner-links">
          {partner.google_maps_url && (
            <a
              href={partner.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-xs tracking-[0.15em] uppercase text-sand/80 border border-lv-border hover:border-bronze/40 transition-colors min-h-[44px]"
              data-testid="partner-directions-link"
            >
              <Map size={14} className="text-bronze/70" />
              Directions
            </a>
          )}
          {partner.website_url && (
            <a
              href={partner.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-xs tracking-[0.15em] uppercase text-sand/80 border border-lv-border hover:border-bronze/40 transition-colors min-h-[44px]"
              data-testid="partner-website-link"
            >
              <ExternalLink size={14} className="text-lv-muted/60" />
              Website
            </a>
          )}
        </div>
      )}

      {/* Introduction */}
      {partner.description && (
        <div className="mt-8 sm:mt-10">
          <p className="text-base sm:text-lg text-sand/85 leading-relaxed font-light tracking-wide">
            {partner.description}
          </p>
        </div>
      )}

      {/* Book a Court CTA — only when dedicated booking_url exists */}
      {partner.booking_url && (
        <div className="mt-8" data-testid="book-a-court">
          <a
            href={partner.booking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="lv-btn-primary inline-flex items-center gap-3 min-h-[48px] px-8 w-full sm:w-auto justify-center"
            data-testid="book-a-court-btn"
          >
            <Calendar size={16} />
            Book a Court
          </a>
        </div>
      )}

      {/* Divider */}
      <div className="my-10 sm:my-12 h-px bg-gradient-to-r from-transparent via-bronze/25 to-transparent" />

      {/* Privileges Section */}
      <div>
        <h2
          className="text-[11px] sm:text-xs tracking-[0.25em] uppercase text-bronze font-medium mb-6 sm:mb-8"
          data-testid="privileges-section-heading"
        >
          {partner.privileges?.length === 1
            ? "Your LV Privilege"
            : "Your LV Privileges"}
        </h2>

        <div className="space-y-5 sm:space-y-6">
          {partner.privileges?.map((pv) => (
            <PrivilegeCard key={pv.id} privilege={pv} />
          ))}
        </div>
      </div>

      {/* Terms */}
      {partner.terms && (
        <div className="mt-10 sm:mt-12" data-testid="partner-terms">
          <div className="flex items-center gap-4 mb-5">
            <div className="h-px flex-1 bg-gradient-to-r from-bronze/20 to-transparent" />
            <span className="text-[10px] tracking-[0.25em] uppercase text-lv-muted/60 font-medium">
              Terms
            </span>
            <div className="h-px flex-1 bg-gradient-to-l from-bronze/20 to-transparent" />
          </div>
          <p className="text-xs text-lv-muted/60 leading-relaxed whitespace-pre-line">
            {partner.terms}
          </p>
        </div>
      )}

      {/* Verification Banner */}
      {isActive && (
        <div className="mt-10 sm:mt-12 bg-walnut-deep border border-lv-border/40 p-5 sm:p-6 flex items-start gap-3" data-testid="verification-banner">
          <Shield size={18} className="text-lv-active shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-cream font-medium tracking-wide">
              Connected to your LV Pass
            </p>
            <p className="text-xs text-lv-muted mt-1 leading-relaxed">
              Present your active LV Pass when requesting the benefit. Your membership is verified in real time.
            </p>
          </div>
        </div>
      )}

      {/* Bottom spacing */}
      <div className="h-8 sm:h-12" />
    </div>
  );
}
